import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, Box, IconButton, Link, ListItemIcon, Menu, MenuItem, Typography } from '@mui/material';
import LanguageIcon from '@mui/icons-material/Language';
import CheckIcon from '@mui/icons-material/Check';

// Endonyms stay untranslated on purpose — each language names itself.
const LANGUAGES = [
    { code: 'de', label: 'Deutsch' },
    { code: 'en', label: 'English' },
];

/**
 * One-line `DE | EN` switcher with the active language marked. Lives in the
 * sidebar's bottom (legal) menu; the language applies immediately on click and
 * the menu stays open, so the relabeling is visible right away.
 */
export function LanguageLinks() {
    const { i18n } = useTranslation();

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, px: 2, py: 0.5 }}>
            {LANGUAGES.map(({ code }, index) => {
                const selected = i18n.resolvedLanguage === code;
                return (
                    <Fragment key={code}>
                        {index > 0 && (
                            <Typography component="span" color="text.disabled">
                                |
                            </Typography>
                        )}
                        <Link
                            component="button"
                            type="button"
                            underline={selected ? 'none' : 'hover'}
                            aria-current={selected ? 'true' : undefined}
                            onClick={() => i18n.changeLanguage(code)}
                            sx={{
                                fontWeight: selected ? 700 : 400,
                                color: selected ? 'primary.main' : 'text.secondary',
                            }}
                        >
                            {code.toUpperCase()}
                        </Link>
                    </Fragment>
                );
            })}
        </Box>
    );
}

function LanguageSwitch() {
    const { t, i18n } = useTranslation();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const currentCode = (i18n.resolvedLanguage || 'de').toUpperCase();

    const handleSelect = (code: string) => {
        i18n.changeLanguage(code); // persisted centrally by the languageChanged listener in i18n.js
        setAnchorEl(null);
    };

    return (
        <>
            <IconButton
                color="inherit"
                title={t('action.changeLanguage')}
                aria-label={t('action.changeLanguage')}
                onClick={(event) => setAnchorEl(event.currentTarget)}
                // the badge bubble protrudes ~11px past the icon's right edge — the margin
                // must clear that overhang or the code visually touches the LOG IN/OUT button
                sx={{ mr: 2.5 }}
            >
                <Badge
                    overlap="circular"
                    badgeContent={<span>{currentCode}</span>}
                    sx={{
                        '& .MuiBadge-badge': {
                            backgroundColor: 'background.paper',
                            color: 'primary.main',
                            fontSize: '0.625rem',
                            fontWeight: 700,
                            height: 16,
                            minWidth: 22,
                            pointerEvents: 'none',
                        },
                    }}
                >
                    <LanguageIcon />
                </Badge>
            </IconButton>
            <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={() => setAnchorEl(null)}>
                {LANGUAGES.map(({ code, label }) => (
                    <MenuItem key={code} selected={i18n.resolvedLanguage === code} onClick={() => handleSelect(code)}>
                        <ListItemIcon sx={{ visibility: i18n.resolvedLanguage === code ? 'visible' : 'hidden' }}>
                            <CheckIcon fontSize="small" />
                        </ListItemIcon>
                        {label}
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
}

export default LanguageSwitch;
